import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';

import selectUser from '../../../../../../store/selectors/appSelector';
import {
  selectEmployeeDocumentation,
  selectHasEmployeeDocumentationError,
  selectHasOnboardingDocumentsError,
  selectIsLoadingEmployeeDocumentation,
  selectIsLoadingOnboardingDocuments,
  selectOnboardingDocuments,
  selectUserDetail,
} from '../../../../../../store/selectors/userListSelector';
import { BLACK, PRIMARY_PURPLE } from '../../../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../../../theme/fontConstants';

import { buildSections } from './documentDisplay';
import { canWriteDocumentReviewState } from './documentReview';
import {
  DocumentDeleteConfirmationModal,
  DocumentExpirationModal,
  DocumentSection,
  DocumentStatusChangeConfirmationModal,
  DocumentUploadHistoryModal,
  DocumentUploadModal,
} from './SubComponents';
import useEmployeeDocumentationActions from './useEmployeeDocumentationActions';

const EmployeeDocumentation = () => {
  const dispatch = useDispatch();
  const selectedUser = useSelector(selectUserDetail)?.[0];
  const sessionUser = useSelector(selectUser);
  const onboardingDocuments = useSelector(selectOnboardingDocuments);
  const isLoadingOnboardingDocuments = useSelector(selectIsLoadingOnboardingDocuments);
  const hasOnboardingDocumentsError = useSelector(selectHasOnboardingDocumentsError);
  const documentHistory = useSelector(selectEmployeeDocumentation);
  const isLoadingDocumentHistory = useSelector(selectIsLoadingEmployeeDocumentation);
  const hasDocumentHistoryError = useSelector(selectHasEmployeeDocumentationError);

  const selectedUserId = selectedUser?.id;
  const { token, refreshToken } = sessionUser || {};

  const {
    activeActionKey,
    documentActionErrorKey,
    expirationDocument,
    handleCancelDelete,
    handleCancelExpiration,
    handleCancelReviewChange,
    handleCloseUploadHistory,
    handleConfirmDelete,
    handleConfirmFormStatusChange,
    handleConfirmFormValidityChange,
    handleOpenUploadHistory,
    handleRequestDelete,
    handleRequestFormStatus,
    handleRequestFormValidity,
    handleRequestSetExpiration,
    handleSaveExpiration,
    handleUploadSuccess,
    handleViewDocument,
    isUploadOpen,
    pendingDeleteDocument,
    pendingFormStatusChange,
    pendingFormValidityChange,
    setIsUploadOpen,
    uploadHistoryDocumentType,
  } = useEmployeeDocumentationActions({
    selectedUserId,
    token,
    refreshToken,
    dispatch,
    canWriteReviewState: canWriteDocumentReviewState(sessionUser?.role),
  });

  const sections = buildSections(onboardingDocuments);
  const uploadHistoryDocuments = uploadHistoryDocumentType
    ? (documentHistory?.documents || []).filter(
        (document) => document?.documentType === uploadHistoryDocumentType
      )
    : [];

  return (
    <Box data-testid="employee-documentation-section">
      <Typography
        sx={{
          fontFamily: PRIMARY_FONT[700],
          fontSize: '20px',
          lineHeight: '100%',
          letterSpacing: '0%',
          color: BLACK[100],
          my: 1,
        }}
      >
        {LanguageConverterV2('userDetail.employeeDocumentation.header')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: PRIMARY_FONT[400],
            fontSize: '16px',
            lineHeight: '100%',
            letterSpacing: '0%',
            color: BLACK[60],
          }}
        >
          {LanguageConverterV2('userDetail.employeeDocumentation.subHeader')}
        </Typography>
        <Button
          startIcon={<UploadFileOutlinedIcon />}
          onClick={() => setIsUploadOpen(true)}
          sx={{
            fontFamily: PRIMARY_FONT[500],
            fontSize: '14px',
            color: PRIMARY_PURPLE[70],
            textTransform: 'none',
            borderRadius: '100px',
            border: `1px solid ${PRIMARY_PURPLE[70]}`,
            px: 2,
            py: 0.5,
            '&:hover': {
              backgroundColor: PRIMARY_PURPLE[10],
            },
          }}
        >
          {LanguageConverterV2('userDetail.employeeDocumentation.upload.button')}
        </Button>
      </Box>

      {isUploadOpen && (
        <DocumentUploadModal
          key={selectedUserId}
          open={isUploadOpen}
          onClose={() => setIsUploadOpen(false)}
          userId={selectedUserId}
          onUploadSuccess={handleUploadSuccess}
        />
      )}

      {(hasOnboardingDocumentsError || documentActionErrorKey) && (
        <Card variant="outlined" sx={{ borderRadius: '16px', mb: 2 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography sx={{ color: BLACK[60], fontFamily: PRIMARY_FONT[400], fontSize: '12px' }}>
              {LanguageConverterV2(
                hasOnboardingDocumentsError
                  ? 'userDetail.employeeDocumentation.error'
                  : documentActionErrorKey
              )}
            </Typography>
          </CardContent>
        </Card>
      )}

      {isLoadingOnboardingDocuments && !onboardingDocuments && (
        <Card variant="outlined" sx={{ borderRadius: '16px', mb: 2 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography sx={{ color: BLACK[60], fontFamily: PRIMARY_FONT[400], fontSize: '12px' }}>
              {LanguageConverterV2('generic.loading')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {onboardingDocuments &&
        sections.map((section) => (
          <DocumentSection
            key={section.key}
            titleKey={section.titleKey}
            documents={section.documents}
            activeActionKey={activeActionKey}
            onView={handleViewDocument}
            onUploadHistory={handleOpenUploadHistory}
            onRequestFormStatus={handleRequestFormStatus}
            onRequestFormValidity={handleRequestFormValidity}
            onRequestSetExpiration={handleRequestSetExpiration}
          />
        ))}

      <DocumentUploadHistoryModal
        open={!!uploadHistoryDocumentType}
        onClose={handleCloseUploadHistory}
        documents={uploadHistoryDocuments}
        onView={handleViewDocument}
        onRequestDelete={handleRequestDelete}
        onRequestFormStatus={handleRequestFormStatus}
        onRequestFormValidity={handleRequestFormValidity}
        onRequestSetExpiration={handleRequestSetExpiration}
        activeActionKey={activeActionKey}
        isLoading={isLoadingDocumentHistory}
        hasError={hasDocumentHistoryError}
        actionErrorKey={documentActionErrorKey}
        disableEnforceFocus={
          !!pendingFormStatusChange ||
          !!pendingFormValidityChange ||
          !!expirationDocument ||
          !!pendingDeleteDocument
        }
      />
      <DocumentDeleteConfirmationModal
        open={!!pendingDeleteDocument}
        isConfirming={activeActionKey?.startsWith('void:')}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
      <DocumentStatusChangeConfirmationModal
        open={!!pendingFormStatusChange || !!pendingFormValidityChange}
        status={pendingFormStatusChange?.status || pendingFormValidityChange?.status}
        isConfirming={
          activeActionKey?.startsWith('setApproval:') || activeActionKey?.startsWith('setValidity:')
        }
        onCancel={handleCancelReviewChange}
        onConfirm={
          pendingFormStatusChange ? handleConfirmFormStatusChange : handleConfirmFormValidityChange
        }
      />
      <DocumentExpirationModal
        open={!!expirationDocument}
        document={expirationDocument}
        isSaving={activeActionKey?.startsWith('setValidityRange:')}
        onCancel={handleCancelExpiration}
        onSave={handleSaveExpiration}
      />
    </Box>
  );
};

export default EmployeeDocumentation;
